// ============================================
// Create Account Prompt
//
// Inline prompt shown after protocol is displayed
// or at the email capture point in the chat flow.
// Offers "Create account to save your results."
//
// When dismissed, saves preference to sessionStorage
// so it doesn't re-appear during the same session.
//
// Design system: light theme card style matching
// CheckoutReview pattern. No framer-motion.
// ============================================

import { useState, useEffect } from 'react';
import { Bookmark, X, Check } from 'lucide-react';
import { PatientAuthForm } from './PatientAuthForm';
import { getCurrentPatientAuth } from '../../services/auth/authUpgradeService';
import { useApp } from '../../context/AppContext';
import { getSessionId } from '../../utils/sessionId';
import { linkJourneyToUid } from '../../services/firestore/journeyService';

const DISMISSED_KEY = 'hemologix_account_prompt_dismissed';

interface CreateAccountPromptProps {
  /** Called after successful account creation */
  onAccountCreated?: (uid: string, email: string) => void;
}

export function CreateAccountPrompt({ onAccountCreated }: CreateAccountPromptProps) {
  const { dispatch } = useApp();
  const [dismissed, setDismissed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [created, setCreated] = useState(false);
  const [alreadyLinked, setAlreadyLinked] = useState(false);

  // Check if already linked or previously dismissed
  useEffect(() => {
    const auth = getCurrentPatientAuth();
    if (auth.isAuthenticated) {
      setAlreadyLinked(true);
      return;
    }
    try {
      const prev = sessionStorage.getItem(DISMISSED_KEY);
      if (prev === 'true') setDismissed(true);
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  // Don't render if already linked or dismissed
  if (alreadyLinked || dismissed) return null;

  // Success state
  if (created) {
    return (
      <div className="bg-[#ECFDF5] border border-[#D1FAE5] rounded-[4px] p-4 my-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
            <Check size={16} className="text-[#059669]" />
          </div>
          <div>
            <p className="text-[14px] font-medium text-[#059669]">Account created</p>
            <p className="text-[13px] text-[#52525B]">
              Your results are saved. Sign in anytime at hemologix.ai/login.
            </p>
          </div>
        </div>
      </div>
    );
  }

  function handleDismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // sessionStorage unavailable
    }
  }

  function handleSuccess(uid: string, email: string) {
    setCreated(true);
    setShowForm(false);

    // Update app state so downstream components know user is authenticated
    dispatch({ type: 'SET_AUTH', payload: true });
    dispatch({ type: 'SET_CHECKOUT_EMAIL', payload: email });

    // Link current journey to authenticated UID (fire-and-forget)
    const sessionId = getSessionId();
    if (!sessionId.startsWith('ephemeral_')) {
      linkJourneyToUid(sessionId, uid).catch((err) =>
        console.warn('[CreateAccountPrompt] Journey UID link failed:', err),
      );
    }

    onAccountCreated?.(uid, email);
  }

  // Expanded form
  if (showForm) {
    return (
      <div className="bg-white border border-[#E4E4E7] rounded-[4px] p-4 my-4 relative" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <button
          onClick={() => setShowForm(false)}
          className="absolute top-3 right-3 text-[#A1A1AA] hover:text-[#52525B] transition-colors"
          aria-label="Close registration form"
        >
          <X size={16} />
        </button>
        <PatientAuthForm
          initialMode="register"
          compact
          onSuccess={handleSuccess}
          onSkip={handleDismiss}
          registerHeading="Save your results"
          registerSubtext="Create an account to access your protocols and lab history anytime."
        />
      </div>
    );
  }

  // Collapsed prompt (teaser)
  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-[4px] p-4 my-4"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-[4px] bg-[#E11D48]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bookmark size={18} className="text-[#E11D48]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[15px] text-[#18181B] font-medium mb-1"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Save your results
          </h3>
          <p className="text-[13px] text-[#52525B] leading-relaxed mb-3">
            Create an account to access your protocol, track labs over time, and reorder supplements.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 rounded-[4px] bg-[#E11D48] text-white text-[13px] font-medium hover:bg-[#BE123C] transition-colors"
            >
              Create Account
            </button>
            <button
              onClick={handleDismiss}
              className="text-[13px] text-[#A1A1AA] hover:text-[#52525B] transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
